/**
 * WHAT: SQL execution history and results viewer for the Custom SQL Executor module.
 * WHY: Provides visibility into past and running SQL executions so DBAs can verify
 *      data extraction success, inspect per-connection results, and diagnose failures
 *      during automated UAR data collection.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api.config';

/**
 * WHAT: SQL execution history viewer with detail and results modals.
 * WHY: DBAs need to monitor and audit SQL executions across databases to ensure
 *      UAR data extraction jobs complete successfully and troubleshoot failures
 *      at the individual connection level.
 */
const SqlExecutions = () => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [executionDetails, setExecutionDetails] = useState(null);
  const [results, setResults] = useState([]);
  const [resultColumns, setResultColumns] = useState(null);  // Column order from API
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    limit: 50,
  });

  const fetchExecutions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      params.append('limit', filters.limit);

      const response = await axios.get(
        `${API_CONFIG.BASE_URL}/api/custom/executions/?${params.toString()}`
      );
      setExecutions(response.data.executions || []);
    } catch (err) {
      console.error('Error fetching executions:', err);
      setError('Failed to load executions');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchExecutions();
    // Auto-refresh every 30 seconds for running executions
    const interval = setInterval(fetchExecutions, 30000);
    return () => clearInterval(interval);
  }, [fetchExecutions]);

  const handleViewDetails = async (execution) => {
    try {
      const response = await axios.get(
        `${API_CONFIG.BASE_URL}/api/custom/executions/${execution.execution_id}/`
      );
      setSelectedExecution(execution);
      setExecutionDetails(response.data);
      setShowDetailsModal(true);
    } catch (err) {
      console.error('Error fetching execution details:', err);
      alert('Failed to load execution details');
    }
  };

  const handleViewResults = async (executionId, detailId = null) => {
    try {
      let url = `${API_CONFIG.BASE_URL}/api/custom/executions/${executionId}/results/?limit=100`;
      if (detailId) url += `&detail_id=${detailId}`;

      const response = await axios.get(url);
      setResults(response.data.results || []);
      setResultColumns(response.data.columns || null);  // Store column order from API
      setShowResultsModal(true);
    } catch (err) {
      console.error('Error fetching results:', err);
      alert('Failed to load results');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      COMPLETED: 'bg-success',
      RUNNING: 'bg-primary',
      PENDING: 'bg-warning text-dark',
      QUEUED: 'bg-info',
      FAILED: 'bg-danger',
      PARTIAL: 'bg-warning text-dark',
      CANCELLED: 'bg-secondary',
      TIMEOUT: 'bg-danger',
    };
    return badges[status] || 'bg-secondary';
  };

  const getTechnologyBadge = (tech) => {
    const badges = {
      ORACLE: 'bg-danger',
      MSSQL: 'bg-primary',
      POSTGRESQL: 'bg-info',
      MYSQL: 'bg-warning text-dark',
      CASSANDRA: 'bg-success',
    };
    return badges[tech] || 'bg-secondary';
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  return (
    <div>
      {/* Header with filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All Status</option>
                <option value="RUNNING">Running</option>
                <option value="COMPLETED">Completed</option>
                <option value="FAILED">Failed</option>
                <option value="PARTIAL">Partial</option>
                <option value="PENDING">Pending</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Limit</label>
              <select
                className="form-select"
                value={filters.limit}
                onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value) })}
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </div>
            <div className="col-md-8 text-end">
              <button className="btn btn-outline-primary" onClick={fetchExecutions}>
                <i className="fas fa-sync me-2"></i>
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {/* Executions Table */}
      <div className="card">
        <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="fas fa-history me-2"></i>
            Execution History ({executions.length})
          </h5>
          {executions.some(e => e.status === 'RUNNING') && (
            <span className="badge bg-primary">
              <i className="fas fa-spinner fa-spin me-1"></i>
              {executions.filter(e => e.status === 'RUNNING').length} Running
            </span>
          )}
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : executions.length > 0 ? (
            <table className="table table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  <th>ID</th>
                  <th>Template</th>
                  <th>Job</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Connections</th>
                  <th className="text-end">Rows</th>
                  <th className="text-center">Duration</th>
                  <th>Started</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {executions.map((exec) => (
                  <tr key={exec.execution_id}>
                    <td>
                      <strong>#{exec.execution_id}</strong>
                      <br />
                      <small className="text-muted">{exec.execution_type}</small>
                    </td>
                    <td>
                      <span className={`badge ${getTechnologyBadge(exec.technology)} me-1`}>
                        {exec.technology}
                      </span>
                      <br />
                      <small>{exec.template_code}</small>
                    </td>
                    <td>
                      {exec.job_code ? (
                        <small>{exec.job_code}</small>
                      ) : (
                        <small className="text-muted">Manual</small>
                      )}
                    </td>
                    <td className="text-center">
                      <span className={`badge ${getStatusBadge(exec.status)}`}>
                        {exec.status === 'RUNNING' && (
                          <i className="fas fa-spinner fa-spin me-1"></i>
                        )}
                        {exec.status}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className="text-success">{exec.successful_connections}</span>
                      <span className="text-muted"> / {exec.total_connections}</span>
                      {exec.failed_connections > 0 && (
                        <span className="text-danger ms-1">
                          ({exec.failed_connections} failed)
                        </span>
                      )}
                    </td>
                    <td className="text-end">
                      {(exec.total_rows_extracted || 0).toLocaleString()}
                    </td>
                    <td className="text-center">
                      {formatDuration(exec.duration_seconds)}
                    </td>
                    <td>
                      <small>
                        {exec.started_at
                          ? new Date(exec.started_at).toLocaleString()
                          : '-'}
                      </small>
                      <br />
                      <small className="text-muted">{exec.executed_by}</small>
                    </td>
                    <td className="text-center">
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => handleViewDetails(exec)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        {exec.total_rows_extracted > 0 && (
                          <button
                            className="btn btn-outline-success"
                            onClick={() => handleViewResults(exec.execution_id)}
                            title="View Results"
                          >
                            <i className="fas fa-table"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-history fa-3x mb-3 opacity-25"></i>
              <p>No executions found</p>
            </div>
          )}
        </div>
      </div>

      {/* Execution Details Modal */}
      {showDetailsModal && executionDetails && (
        <div
          className="modal fade show"
          style={{
            display: 'block',
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            overflow: 'auto',
          }}
        >
          <div style={{ padding: '20px' }}>
            <div
              className="modal-content"
              style={{ maxWidth: '1200px', width: '100%', margin: '0 auto' }}
            >
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="fas fa-info-circle me-2"></i>
                  Execution #{executionDetails.execution_id} Details
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowDetailsModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {/* Summary */}
                <div className="row mb-4">
                  <div className="col-md-3">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <h6 className="text-muted mb-1">Status</h6>
                        <span className={`badge ${getStatusBadge(executionDetails.status)} fs-6`}>
                          {executionDetails.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <h6 className="text-muted mb-1">Connections</h6>
                        <h4 className="mb-0">
                          <span className="text-success">{executionDetails.successful_connections}</span>
                          <span className="text-muted"> / {executionDetails.total_connections}</span>
                        </h4>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <h6 className="text-muted mb-1">Rows Extracted</h6>
                        <h4 className="mb-0">
                          {(executionDetails.total_rows_extracted || 0).toLocaleString()}
                        </h4>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <h6 className="text-muted mb-1">Duration</h6>
                        <h4 className="mb-0">
                          {formatDuration(executionDetails.duration_seconds)}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <th>Template:</th>
                          <td>{executionDetails.template_code} - {executionDetails.template_name}</td>
                        </tr>
                        <tr>
                          <th>Technology:</th>
                          <td>
                            <span className={`badge ${getTechnologyBadge(executionDetails.technology)}`}>
                              {executionDetails.technology}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th>Type:</th>
                          <td>{executionDetails.execution_type}</td>
                        </tr>
                        <tr>
                          <th>Mode:</th>
                          <td>{executionDetails.execution_mode}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <th>Job:</th>
                          <td>{executionDetails.job_code || '-'}</td>
                        </tr>
                        <tr>
                          <th>Started:</th>
                          <td>
                            {executionDetails.started_at
                              ? new Date(executionDetails.started_at).toLocaleString()
                              : '-'}
                          </td>
                        </tr>
                        <tr>
                          <th>Completed:</th>
                          <td>
                            {executionDetails.completed_at
                              ? new Date(executionDetails.completed_at).toLocaleString()
                              : '-'}
                          </td>
                        </tr>
                        <tr>
                          <th>Executed By:</th>
                          <td>{executionDetails.executed_by}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Connection Details */}
                <h6 className="mb-3">
                  <i className="fas fa-database me-2"></i>
                  Connection Details ({executionDetails.details?.length || 0})
                </h6>
                {executionDetails.details?.length > 0 ? (
                  <table className="table table-sm table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th>Connection</th>
                        <th className="text-center">Status</th>
                        <th className="text-end">Rows</th>
                        <th className="text-center">Time</th>
                        <th>Error</th>
                        <th className="text-center">Results</th>
                      </tr>
                    </thead>
                    <tbody>
                      {executionDetails.details.map((detail) => (
                        <tr key={detail.detail_id}>
                          <td>
                            <strong>{detail.connection_name}</strong>
                            <br />
                            <small className="text-muted">
                              {detail.database_identifier}
                            </small>
                          </td>
                          <td className="text-center">
                            <span className={`badge ${getStatusBadge(detail.status)}`}>
                              {detail.status}
                            </span>
                          </td>
                          <td className="text-end">
                            {(detail.rows_returned || 0).toLocaleString()}
                          </td>
                          <td className="text-center">
                            {detail.execution_time_ms
                              ? `${(detail.execution_time_ms / 1000).toFixed(2)}s`
                              : '-'}
                          </td>
                          <td>
                            {detail.error_message ? (
                              <small className="text-danger" title={detail.error_message}>
                                {detail.error_message.substring(0, 50)}...
                              </small>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="text-center">
                            {detail.rows_returned > 0 && (
                              <button
                                className="btn btn-sm btn-outline-success"
                                onClick={() => handleViewResults(executionDetails.execution_id, detail.detail_id)}
                              >
                                <i className="fas fa-table"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-muted">No connection details available</p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Modal */}
      {showResultsModal && (
        <div
          className="modal fade show"
          style={{
            display: 'block',
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1060,
            overflow: 'auto',
          }}
        >
          <div style={{ padding: '20px' }}>
            <div
              className="modal-content"
              style={{ maxWidth: '1400px', width: '100%', margin: '0 auto' }}
            >
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">
                  <i className="fas fa-table me-2"></i>
                  Execution Results ({results.length} rows)
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowResultsModal(false)}
                ></button>
              </div>
              <div className="modal-body p-0">
                {results.length > 0 ? (
                  <div className="table-responsive" style={{ maxHeight: '600px' }}>
                    <table className="table table-sm table-striped table-hover mb-0">
                      <thead className="table-dark" style={{ position: 'sticky', top: 0 }}>
                        <tr>
                          <th style={{ width: '60px' }}>#</th>
                          {(() => {
                            // Use column order from API if available, otherwise extract from first row
                            if (resultColumns && Array.isArray(resultColumns)) {
                              return resultColumns.map((col) => (
                                <th key={col} style={{ whiteSpace: 'nowrap' }}>{col}</th>
                              ));
                            }
                            // Fallback: extract columns from first row
                            const firstRowData = results[0]?.row_data;
                            const parsedData = typeof firstRowData === 'string'
                              ? JSON.parse(firstRowData)
                              : firstRowData;
                            return Object.keys(parsedData || {}).map((col) => (
                              <th key={col} style={{ whiteSpace: 'nowrap' }}>{col}</th>
                            ));
                          })()}
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((result) => {
                          // Parse row_data if it's a string
                          const rowData = typeof result.row_data === 'string'
                            ? JSON.parse(result.row_data)
                            : result.row_data;

                          // Use column order if available
                          const columns = resultColumns && Array.isArray(resultColumns)
                            ? resultColumns
                            : Object.keys(rowData || {});

                          return (
                            <tr key={result.result_id}>
                              <td className="text-muted">{result.row_number}</td>
                              {columns.map((col, idx) => {
                                const val = rowData ? rowData[col] : null;
                                return (
                                  <td key={idx}>
                                    {val !== null && val !== undefined ? String(val) : <span className="text-muted">-</span>}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-inbox fa-3x mb-3 opacity-25"></i>
                    <p>No results available</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <span className="text-muted me-auto">
                  <i className="fas fa-info-circle me-1"></i>
                  Showing {results.length} rows (limit: 100)
                </span>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowResultsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SqlExecutions;
